import { Box, Stack, Typography } from '@montugroup/design-system';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { styled } from '@mui/material';

import { outOfBandThemeColors } from '@/theme/colors';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared';
import React from 'react';
import { formatPriceForBrevity } from '../../utils';
import { ShippingOptionAlertPresence } from './ShippingOptionAlertPresence';
import { ShippingOptionAlertValidId } from './ShippingOptionAlertValidId';

export type DeliveryOptionUiSettings = {
  title: string;
  selected: boolean;
  isEnabled: boolean;
  estimatedArrivalTime?: string;
};

export type ShippingOptionProps = Omit<
  React.ComponentProps<typeof ShippingOptionCard>,
  'selected' | 'onClick' | 'hasError'
> & {
  shippingPrice: number;
  field: {
    value: string | undefined;
    onChange: (value: string) => void;
  };
  fieldState: {
    error?: { message?: string };
  };
  deliveryRequirements?: {
    identification?: boolean;
    presence?: boolean;
  };
  onClick?: () => void;
};

type ShippingOptionCardProps = {
  icon: React.ReactNode;
  title: React.ReactNode;
  shippingPrice: number;
  description: React.ReactNode;
  ShippingOptionAlert?: React.JSXElementConstructor<{ selected?: boolean }>;
  onClick?: () => void;
  selected?: boolean;
  name: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
  hasError?: boolean;
  deliveryRequirements?: {
    identification?: boolean;
    presence?: boolean;
  };
  discountedPrice?: number | null;
};

const ShippingOptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'hasError',
})<{ selected?: boolean; hasError?: boolean }>(({ theme, selected, hasError }) => ({
  border: selected ? '2px solid' : '1px solid',
  borderRadius: theme.shape.borderRadius,
  borderColor: hasError
    ? theme.palette.error.main
    : selected
      ? theme.palette.text.primary
      : theme.palette.action.disabled,
  backgroundColor: selected ? outOfBandThemeColors.uMeds.timberGreen100 : theme.palette.background.paper,
}));

const ShippingOptionContentContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(1),
  padding: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: `"control header"
        ". content"`,
  cursor: 'pointer',
}));

const ShippingOptionContent = styled(Box)(({ theme }) => ({
  gridArea: 'content',
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  marginTop: theme.spacing(2),
  justifyContent: 'space-between',
}));

const ShippingOptionHeader = styled(Box)(() => ({
  gridArea: 'header',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ShippingOptionPrice = ({ price, discountedPrice }: { price: number; discountedPrice?: number | null }) => {
  if (discountedPrice !== null && discountedPrice !== undefined) {
    return (
      <Stack direction={'row'} gap={1}>
        <Typography fontWeight="bold" color="success.main" data-testid="shippingOptionsFee">
          {discountedPrice === 0 ? 'Free' : `$${formatPriceForBrevity(discountedPrice)}`}
        </Typography>{' '}
        <Typography component="s" color="text.disabled">
          ${formatPriceForBrevity(price)}
        </Typography>
      </Stack>
    );
  }

  return (
    <Typography fontWeight="bold" data-testid="shippingOptionsFee">
      {price === 0 ? 'Free' : `$${formatPriceForBrevity(price)}`}
    </Typography>
  );
};

export function ShippingOptionAlert({
  selected,
  deliveryRequirements,
  deliveryOptionName,
}: {
  selected: boolean;
  deliveryRequirements?: {
    identification?: boolean;
    presence?: boolean;
  };
  deliveryOptionName: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
}) {
  if (!selected) {
    return null;
  }

  if (deliveryRequirements?.identification) {
    return <ShippingOptionAlertValidId deliveryOptionName={deliveryOptionName} />;
  }

  if (deliveryRequirements?.presence) {
    return <ShippingOptionAlertPresence deliveryOptionName={deliveryOptionName} />;
  }
  return null;
}

export function ShippingOptionCard({
  icon,
  title,
  shippingPrice,
  description,
  name,
  onClick,
  selected,
  hasError,
  deliveryRequirements,
  discountedPrice,
}: ShippingOptionCardProps) {
  return (
    <ShippingOptionContainer selected={selected} data-testid="shippingOptions" hasError={hasError}>
      <ShippingOptionContentContainer onClick={onClick}>
        <Box
          sx={{
            width: 24,
            height: 24,
          }}
        >
          {icon}
        </Box>
        <ShippingOptionHeader>
          <Typography aria-hidden>
            <Box component="span" fontWeight="bold" data-testid="shippingOptionsTitle">
              {title}
            </Box>
          </Typography>

          <ShippingOptionPrice price={shippingPrice} discountedPrice={discountedPrice} />
        </ShippingOptionHeader>

        <ShippingOptionContent>
          <Typography>
            <Box data-testid="shippingOptionsDescription">{description}</Box>
          </Typography>
          <Box>
            {name === DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name && (
              <ArrowForwardIosIcon fontSize="small" />
            )}
          </Box>
        </ShippingOptionContent>
      </ShippingOptionContentContainer>
      <ShippingOptionAlert
        selected={!!selected}
        deliveryRequirements={deliveryRequirements}
        deliveryOptionName={name}
      />
    </ShippingOptionContainer>
  );
}
